import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import { Chevron } from './Charts.jsx'
import { TeamMark } from './Marks.jsx'
import SeasonHead, { ViewFrame } from './SeasonHead.jsx'
import { teamStyle } from './colors.js'
import { useHead, useHistoryResource, useSeasonChoice, useTeams } from './historyStore.jsx'
import { playersList } from './leagueModel.js'
import { playerPath } from './paths.js'
import { READY } from './poll.js'
import { backState } from './useBack.js'
import { useDocumentTitle } from '../../useDocumentTitle.js'

// Players: the index of everyone who played that season, with search. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// THE LIST IS THE SEASON'S REAL INDEX, AND THE SEARCH FOLDS ACCENTS
// ═══════════════════════════════════════════════════════════════════════════
//
// `players_index.json` has every player that season (582 in 2025-26), which the design's stand-in, built from what
// its samples happened to mention, did not. Search runs over it in the page (`leagueModel.js::playersList`): the
// typed text and each name are folded (accents off, case off), so `doncic` finds `Dončić` and `demin` finds `Dëmin`,
// and a phone's keyboard, which cannot type a caron, is not a reason to find nobody.
//
// A ROW IS A LINK, NOT A BUTTON. Opening a player is a navigation to `/hoops/players/:personId?season=…`, so it can
// be middle-clicked, long-pressed, copied and pasted into Discord, and the phone's Back gesture returns to this list
// with the same season (the link carries where it came from: `useBack.js`).
//
// ═══════════════════════════════════════════════════════════════════════════
// WHAT WAS TYPED IS IN THE ADDRESS (`?q=`), SO BACK RETURNS TO THE LIST THE MEMBER WAS LOOKING AT
// ═══════════════════════════════════════════════════════════════════════════
//
// The index is 582 names and nobody scrolls it: the way in is the search box, then a tap. A Back that lands on the full
// list with the box empty throws away the one thing the member did, so the query is part of the page's address, and
// the list the member left is the list Back returns to (and the one a pasted `?q=doncic` opens on).
//
// THE BOX KEEPS ITS OWN STATE AND THE ADDRESS FOLLOWS IT, NEVER THE OTHER WAY. A controlled input bound to the
// router's state is a known way to lose keystrokes (router updates run in a transition, and a controlled input that
// reads a deferred value rolls back to it), so the box is local, initialised once from the address, and the address is
// written from it with `replace` after a short pause: typing is one history entry's worth of change, not one entry per
// letter. `history.replaceState` is rate-limited by browsers (Safari throws past 100 calls in 30 seconds, and the
// router answers a throw by reloading the page), which a held Backspace could reach and a pause cannot.
//
// A row tapped inside that pause writes the address FIRST (`flush`, from the row's own `onClick`, which runs before the
// link navigates), so the entry Back returns to always has the query the member saw. The read is capped at `QUERY_MAX`
// characters: it is a filter over names and the address is untrusted text.

const NON_BREAKING_HYPHEN = '‑'
/** How long the box waits after the last keystroke before it writes the address. */
export const TYPING_PAUSE_MS = 250
/** The most the address's `q` is read as. A name is not longer than this. */
export const QUERY_MAX = 60

function PlayersBody() {
  useDocumentTitle('Players')
  const choice = useSeasonChoice()
  const teams = useTeams()
  const season = choice.selected?.season ?? null
  const index = useHistoryResource('index', season)
  const location = useLocation()
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState(() => (params.get('q') ?? '').slice(0, QUERY_MAX))
  const pause = useRef(null)
  const unwritten = useRef(null)
  const latest = useRef(null)
  const ready = index.state === READY
  const head = useHead({ season, entry: choice.selected, asOf: index.data?.as_of, source: index.data?.source, ready })

  // A timer that fires after this view has gone would write the query into whatever page the member is on by then.
  useEffect(() => () => clearTimeout(pause.current), [])

  if (choice.selected === null) return null
  const list = ready ? playersList(index.data, query) : null

  /** Write `value` into the address: `q` set, or removed when the box is empty. The entry is replaced, never added to. */
  const write = (value) => {
    unwritten.current = null
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous)
        if (value === '') next.delete('q')
        else next.set('q', value)
        return next
      },
      { replace: true, state: location.state },
    )
  }
  // The pause's timer calls the NEWEST `write`, not the one from the render that typed: the router's setter closes over the
  // address as of its own render, so a season chosen inside the pause would otherwise be written back over.
  latest.current = write
  const type = (value) => {
    setQuery(value)
    unwritten.current = value
    clearTimeout(pause.current)
    pause.current = setTimeout(() => latest.current(value), TYPING_PAUSE_MS)
  }
  /** Before leaving for a player's page: whatever was typed and not yet written goes into the entry Back returns to. */
  const flush = () => {
    clearTimeout(pause.current)
    if (unwritten.current !== null) write(unwritten.current)
  }

  return (
    <div className="d2d-hoops__view" data-testid="hoops-players" data-stale={head.stale ? 'true' : 'false'}>
      <SeasonHead
        choice={choice}
        unavailable={NONE}
        head={head}
        title={list ? `${list.total} players in the ${season.replace('-', NON_BREAKING_HYPHEN)} index.` : null}
        back={null}
      />
      {list === null ? null : (
        <>
          <label className="d2d-hoops__search">
            <span className="d2d-hoops__searchlabel d2d-micro">Search by name {'·'} {season}</span>
            <input
              type="search"
              className="d2d-hoops__searchinput"
              value={query}
              onChange={(event) => type(event.target.value)}
              placeholder="Name"
              autoComplete="off"
              spellCheck={false}
              data-testid="hoops-players-search"
            />
          </label>
          {list.none ? (
            <p className="d2d-hoops__none" data-testid="hoops-players-none">
              No one in the {season} index matches {'“'}
              {list.typed}
              {'”'}.
            </p>
          ) : null}
          <div className="d2d-hoops__itemgrid" data-testid="hoops-players-list">
            {list.rows.map((row) => (
              <Link
                key={row.id}
                to={playerPath(row.id, season)}
                state={backState('Players')}
                onClick={flush}
                className="d2d-hoops__item"
                style={teamStyle(teams.byId(row.teamId))}
                data-testid="hoops-player-row"
                data-person-id={row.id}
              >
                <TeamMark team={teams.byId(row.teamId)} tricode={row.team} which="away" />
                <span className="d2d-hoops__itemtext">
                  <span className="d2d-hoops__itemname">{row.name}</span>
                  <span className="d2d-hoops__itemsub d2d-micro">{row.sub}</span>
                </span>
                <Chevron direction="right" />
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const NONE = new Set()

export default function PlayersView() {
  return (
    <ViewFrame tab="players">
      <PlayersBody />
    </ViewFrame>
  )
}
